import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, DialogService } from '@teammood/shared/ui/dialog';
import { Team } from '../team.type';
import { BatchUpdateProperty, TeamMemberBatchUpdateDialogComponent, TeamMemberBatchUpdateDialogData } from './team-member-batch-update-dialog/team-member-batch-update-dialog.component';
import { TeamMemberDialogData, TeamMemberFormDialogComponent } from './team-member-form-dialog/team-member-form-dialog.component';
import {
  TeamMemberMoveDialogData,
  TmTeamMemberMoveDialogComponent
} from './team-member-move/team-member-move-dialog.component';
import { TeamMemberService } from './team-member.service';
import { TeamMember } from './team-member.type';
import {
  TeamMemberDeleteDialogData,
  TeamMemberDeleteDialogComponent
} from "./team-member-delete-dialog/team-member-delete-dialog.component";

@Injectable(
  { providedIn: 'root' }
)
export class TeamMemberDialogService {

  constructor(
    private _dialogService: DialogService,
    private _translateService: TranslateService,
    private _teamMemberService: TeamMemberService
  ) {
  }

  async create(team: Team): Promise<MatDialogRef<TeamMemberFormDialogComponent, TeamMember | boolean>> {
    const memberDefaults = {
      active: true,
      roles: ['member'],
      notificationTime: team.notificationTime,
      workingDays: team.workingDays,
      language: team.language
    };

    return await this._openUpsertDialog(memberDefaults, team, true);
  }

  async update(team: Team, member: TeamMember): Promise<MatDialogRef<TeamMemberFormDialogComponent, TeamMember | boolean>> {
    return await this._openUpsertDialog(member, team, false);
  }

  async delete(member: Partial<TeamMember>): Promise<MatDialogRef<TeamMemberDeleteDialogComponent, 'disable' | 'delete'>> {
    return await this._dialogService.open<TeamMemberDeleteDialogComponent, TeamMemberDeleteDialogData, 'disable' | 'delete'>(TeamMemberDeleteDialogComponent, {
      width: '500px',
      data: {
        member
      }
    });
  }

  async move(team: Team, member: TeamMember): Promise<MatDialogRef<TmTeamMemberMoveDialogComponent, TeamMember>> {
    const teams = await this._teamMemberService.getAvailableTeamsForMove(member.id, team.id);

    return await this._dialogService.open<TmTeamMemberMoveDialogComponent, TeamMemberMoveDialogData, TeamMember>(TmTeamMemberMoveDialogComponent, {
      width: '500px',
      data: {
        member,
        team,
        teams
      }
    });
  }

  async batchUpdate(team: Team, selectedTeamMembers: TeamMember[], property: BatchUpdateProperty, width: string = '500px'): Promise<MatDialogRef<TeamMemberBatchUpdateDialogComponent, boolean>> {
    return await this._dialogService.open<TeamMemberBatchUpdateDialogComponent, TeamMemberBatchUpdateDialogData, boolean>(TeamMemberBatchUpdateDialogComponent, {
      width: width,
      data: {
        property: property,
        teamId: team.id,
        selectedTeamMembers
      }
    })
  }

  private _openUpsertDialog(member: Partial<TeamMember>, team: Team, creation: boolean) {
    return this._dialogService.open<TeamMemberFormDialogComponent, TeamMemberDialogData, TeamMember | boolean>(TeamMemberFormDialogComponent, {
      width: '750px',
      height: '100%',
      maxWidth: '100%',
      position: { right: '0', top: '0', bottom: '0' },
      enterAnimationDuration: 0,
      exitAnimationDuration: 0,
      data: {
        member: member,
        team: team,
        creation: creation
      }
    });
  }
}